import React, { useState, useEffect } from "react";
import axios from "axios";
import { base_url } from "../utils/axiosConfig";
import { toast } from "react-toastify";

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user_website") || "{}");
      const token = userData.token;

      if (!token) throw new Error("No token found. Please log in.");

      const response = await axios.get(
        `${base_url}product/getOrderForEndUser`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOrders(Array.isArray(response.data.data) ? response.data.data : []);
      setLoading(false);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to fetch orders",
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const initiatePayment = async ({ id }) => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${base_url}cashfree/create-order-EU-repayment`,
        { id },
      );

      const { paymentSessionId } = response.data;

      if (!paymentSessionId)
        throw new Error("No payment session received from server");

      const cashfree = new window.Cashfree();

      cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_self",
        mode: "production",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Payment failed. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p>Loading your orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-green-600">
          Your Order History
        </h1>

        {!orders.length ? (
          <div className="text-center bg-white p-6 rounded shadow">
            <p>No orders found.</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order._id} className="bg-white shadow rounded-lg mb-6">
              {/* ORDER HEADER */}
              <div className="bg-green-100 p-4 flex justify-between items-center">
                <div>
                  <h2 className="font-bold">Order #{order._id.slice(-6)}</h2>
                  <p className="text-sm">
                    {new Date(order.createdAt).toLocaleDateString("en-IN")}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      order.status === "approved"
                        ? "bg-green-200 text-green-700"
                        : order.status === "paymentPending"
                          ? "bg-yellow-200 text-yellow-700"
                          : "bg-red-200 text-red-700"
                    }`}
                  >
                    {order.status
                      ? order.status.charAt(0).toUpperCase() +
                        order.status.slice(1)
                      : "Unknown"}
                  </span>
                  {order.status === "approved" && order.invoiceUrl && (
                    <a
                      href={order.invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      Download Invoice
                    </a>
                  )}

                  {(order.transaction.status !== "PAID" &&
                    !order.invoiceUrl) && (
                    <button
                      onClick={() => initiatePayment({ id: order._id })}
                      className="px-4 py-2 bg-green-600 text-white rounded"
                    >
                      Pay Now
                    </button>
                  )}
                </div>
              </div>

              {/* ORDER BODY */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {(order.products || []).map((product) => {
                    const productDetails = product.productDetails;

                    if (!productDetails) {
                      return (
                        <div
                          key={product._id}
                          className="bg-red-100 text-red-600 p-4 rounded"
                        >
                          Product no longer available
                        </div>
                      );
                    }

                    const variation =
                      productDetails?.variations?.find(
                        (v) => v._id === product.variationId,
                      ) || null;

                    const productImage =
                      variation?.images?.[0] ||
                      productDetails?.images?.[0] ||
                      "https://via.placeholder.com/300";

                    const variationDetails =
                      productDetails.productType === "digital"
                        ? "Digital Product"
                        : variation
                          ? `Size: ${variation.size || "N/A"}, Color: ${
                              variation.color || "N/A"
                            }`
                          : "Default Variant";

                    return (
                      <div
                        key={product._id}
                        className="bg-white border rounded p-4 shadow-sm"
                      >
                        <img
                          src={productImage}
                          alt={productDetails.productName}
                          className="w-full h-40 object-cover rounded mb-3"
                          onError={(e) =>
                            (e.target.src = "https://via.placeholder.com/300")
                          }
                        />

                        <h4 className="font-semibold mb-2">
                          {productDetails.productName}
                        </h4>

                        <p className="text-sm mb-1">
                          <strong>Source:</strong>{" "}
                          {product.productSource === "vendor"
                            ? "Vendor Product"
                            : "Company Product"}
                        </p>

                        <p className="text-sm mb-1">
                          <strong>Variation:</strong> {variationDetails}
                        </p>

                        <div className="flex justify-between mt-2">
                          <span>Qty: {product.quantity}</span>
                          <span className="text-green-600 font-semibold">
                            ₹{(product.pricePerProduct || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* TOTAL */}
                <div className="mt-6 text-right">
                  <h3 className="text-lg font-bold text-green-600">
                    Total: ₹{(order.totalAmount || 0).toFixed(2)}
                  </h3>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
